import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { AsyncLoadStatus } from '../common/types';
import LoadingIndicator from '../components/LoadingIndicator';
import { ContainerMain } from '../components/layout/ContainerMain';
import { useLoadLayout } from '../layout/hooks/useLoadLayout';
import { appIsEmbeddedState } from '../store/global.state';
import ActualityLayout from './ActualityLayout';
import FeedHeader from './FeedHeader';
import useActualityStatusWatcher from './hooks/useActualityStatusWatcher';
import useActualityTimeWatcher from './hooks/useActualityTimeWatcher';
import { useLoadActuality } from './hooks/useLoadActuality';
import { useLoadActualityAiQueries } from './hooks/useLoadActualityAiQueries';
import {
  actualitySelectedDocument,
  actualitySelectedDocumentStatus,
} from './store/actuality.state';
import { feedLayoutId, feedSelectedDateRange } from './store/feed.state';

type ActualityRootParams = {
  actualityId: string;
  isEmbedded: string;
};

export const ActualityRoot: React.FC = () => {
  const { t } = useTranslation('feed');
  const { actualityId, isEmbedded: isEmbeddedParam } =
    useParams<ActualityRootParams>();
  const actuality = useRecoilValue(actualitySelectedDocument);
  const actualityLoadStatus = useRecoilValue(actualitySelectedDocumentStatus);
  const resetSelectedDateRange = useResetRecoilState(feedSelectedDateRange);
  const isEmbeddedAtom = useRecoilValue(appIsEmbeddedState);
  const layoutId = useRecoilValue(feedLayoutId);

  const [loadLayout, cleanupLayout] = useLoadLayout();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadActuality, unloadActuality] = useLoadActuality();
  const [loadActualityAiQueries, cleanupActualityAiQueries] =
    useLoadActualityAiQueries();

  useActualityTimeWatcher();
  useActualityStatusWatcher();

  useEffect(() => {
    if (!_.isNil(actualityId)) {
      resetSelectedDateRange();
      loadActuality(actualityId);
      loadActualityAiQueries({ actualityId });
    }
    return () => {
      unloadActuality();
      cleanupActualityAiQueries();
    };
  }, [actualityId]);

  useEffect(() => {
    if (!_.isNil(layoutId)) {
      loadLayout(layoutId);
    }
    return () => {
      cleanupLayout();
    };
  }, [layoutId]);

  const hasHeader =
    _.isNil(searchParams.get('token')) &&
    _.isNil(isEmbeddedParam) &&
    !isEmbeddedAtom;

  const isReady = actualityLoadStatus === AsyncLoadStatus.Ok;

  if (!isReady) {
    return (
      <>
        {hasHeader && <FeedHeader />}
        <ContainerMain data-component="ActualityRoot">
          <LoadingIndicator selfCenter message={t`Loading Data...`} />
        </ContainerMain>
      </>
    );
  }

  return <ActualityLayout hasHeader={hasHeader} />;
};

export default ActualityRoot;
